import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../firebaseconfig';
import { fetchCategories } from '../utils/categories';
import { getExpensesWithStock } from '../utils/expenseStockIntegration';
import './ExpenseReport.css';
import { 
  FaQuestion,
  FaUtensils,
  FaGraduationCap,
  FaShoppingBasket,
  FaLightbulb,
  FaTint,
  FaUsers,
  FaCoffee,
  FaWineGlass,
  FaWater,
  FaToolbox,
  FaHandHoldingWater,
  FaHandHoldingHeart
} from 'react-icons/fa';

const ExpenseReport = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [reportData, setReportData] = useState(null);
  const [expensesWithStock, setExpensesWithStock] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [showStockAnalysis, setShowStockAnalysis] = useState(false);
  const availableIcons = [
    { name: 'FaQuestion', icon: <FaQuestion /> },
    { name: 'FaUtensils', icon: <FaUtensils /> },
    { name: 'FaGraduationCap', icon: <FaGraduationCap /> },
    { name: 'FaShoppingBasket', icon: <FaShoppingBasket /> },
    { name: 'FaLightbulb', icon: <FaLightbulb /> },
    { name: 'FaTint', icon: <FaTint /> },
    { name: 'FaUsers', icon: <FaUsers /> },
    { name: 'FaCoffee', icon: <FaCoffee /> },
    { name: 'FaWineGlass', icon: <FaWineGlass /> },
    { name: 'FaWater', icon: <FaWater /> },
    { name: 'FaToolbox', icon: <FaToolbox /> },
    { name: 'FaHandHoldingWater', icon: <FaHandHoldingWater /> },
    { name: 'FaHandHoldingHeart', icon: <FaHandHoldingHeart /> }
  ];
  const getIconComponent = (iconName) => {
    const icon = availableIcons.find(i => i.name === iconName);
    return icon ? icon.icon : <FaQuestion />;
  };

  useEffect(() => {
    const q = query(collection(db, 'expenses'), orderBy('date', 'desc'));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      try {
        const expensesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setExpenses(expensesData);
        setLoading(false);
      } catch (err) {
        setError('Error loading expenses');
        setLoading(false);
        console.error('Error fetching expenses:', err);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const fetched = await fetchCategories();
        setCategories(fetched);
      } catch (e) {
        console.error('Error loading categories:', e);
        setError('Error loading categories');
      }
    };
    loadCategories();
    
    // Load expenses with stock and stock items
    const loadStockData = async () => {
      try {
        const [stockExpenses, stockItemsSnapshot] = await Promise.all([
          getExpensesWithStock(),
          getDocs(query(collection(db, 'stock_items'), orderBy('name', 'asc')))
        ]);
        setExpensesWithStock(stockExpenses);
        setStockItems(stockItemsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));
      } catch (e) {
        console.error('Error loading stock data:', e);
      }
    };
    loadStockData();
  }, []);

  const generateReport = () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    // Validate that end date is not before start date
    if (new Date(endDate) < new Date(startDate)) {
      setError('End date cannot be before start date');
      return;
    }

    const filteredExpenses = expenses.filter(expense => {
      // Handle different date formats from Firestore
      let expenseDate;
      if (expense.date?.toDate) {
        // Firestore Timestamp
        expenseDate = expense.date.toDate();
      } else if (expense.date) {
        // Regular Date object or string
        expenseDate = new Date(expense.date);
      } else if (expense.createdAt?.toDate) {
        // Fallback to createdAt if date is not available
        expenseDate = expense.createdAt.toDate();
      } else if (expense.createdAt) {
        expenseDate = new Date(expense.createdAt);
      } else {
        return false; // Skip expenses without date
      }

      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      
      // Set time to start and end of day for proper comparison
      startDateObj.setHours(0, 0, 0, 0);
      endDateObj.setHours(23, 59, 59, 999);
      
      const dateMatch = expenseDate >= startDateObj && expenseDate <= endDateObj;
      
      // Filter by category if selected
      const categoryMatch = selectedCategory === 'all' || expense.category === selectedCategory;
      
      return dateMatch && categoryMatch;
    });

    console.log('Date filtering:', {
      startDate,
      endDate,
      totalExpenses: expenses.length,
      filteredCount: filteredExpenses.length,
      sampleExpense: expenses[0]
    });

    const categoryTotals = {};
    let totalAmount = 0;

    filteredExpenses.forEach(expense => {
      const { category, amount } = expense;
      categoryTotals[category] = (categoryTotals[category] || 0) + amount;
      totalAmount += amount;
    });

    const dailyExpenses = {};
    filteredExpenses.forEach(expense => {
      const date = expense.date;
      if (!dailyExpenses[date]) {
        dailyExpenses[date] = 0;
      }
      dailyExpenses[date] += expense.amount;
    });

    const averageDailyExpense = totalAmount / Object.keys(dailyExpenses).length || 0;

    const categoryBreakdown = Object.entries(categoryTotals)
      .map(([categoryId, amount]) => {
        const category = categories.find(c => c.id === categoryId) || { id: categoryId, name: 'Other', color: '#95A5A6', icon: '📦' };
        return {
          ...category,
          amount,
          percentage: totalAmount > 0 ? (amount / totalAmount) * 100 : 0
        };
      })
      .sort((a, b) => b.amount - a.amount);

    setReportData({
      totalAmount,
      averageDailyExpense,
      categoryBreakdown,
      expenses: filteredExpenses,
      startDate,
      endDate
    });
    setError('');
  };

  const formatDate = (dateInput) => {
    let date;
    if (dateInput?.toDate) {
      // Firestore Timestamp
      date = dateInput.toDate();
    } else if (dateInput) {
      // Regular Date object or string
      date = new Date(dateInput);
    } else {
      return 'No date';
    }
    
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  // Download report function (currently commented out in UI)
  // const downloadReport = () => {
  //   if (!reportData) return;
  //
  //   const report = `Expense Report (${formatDate(reportData.startDate)} - ${formatDate(reportData.endDate)})
  //   
  // Total Expenses: ${formatAmount(reportData.totalAmount)}
  // Average Daily Expenses: ${formatAmount(reportData.averageDailyExpense)}
  //
  //  Item Breakdown:
  //  ${reportData.categoryBreakdown.map(cat => 
  //    `${cat.name}: ${formatAmount(cat.amount)} (${cat.percentage.toFixed(1)}%)`
  //  ).join('\n')}
  //
  // Detailed Expenses:
  // ${reportData.expenses.map(expense => 
  //   `${formatDate(expense.date)} - ${(categories.find(c => c.id === expense.category)?.name) || 'Other'} - ${formatAmount(expense.amount)}`
  // ).join('\n')}
  // `;
  //
  //   const blob = new Blob([report], { type: 'text/plain' });
  //   const url = window.URL.createObjectURL(blob);
  //   const a = document.createElement('a');
  //   a.href = url;
  //   a.download = `expense-report-${reportData.startDate}-to-${reportData.endDate}.txt`;
  //   document.body.appendChild(a);
  //   a.click();
  //   document.body.removeChild(a);
  //   window.URL.revokeObjectURL(url);
  // };

  if (loading) {
    return <div className="loading">Loading expenses...</div>;
  }

  return (
    <div className="report-container">
      <div className="report-header">
        <h2>Expense Report</h2>
        <div className="date-filters">
          <div className="date-input">
            <label htmlFor="start-date">Start Date</label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="date-input">
            <label htmlFor="end-date">End Date</label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="category-filter">
            <label htmlFor="category-filter">Category</label>
            <select
              id="category-filter"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="category-select"
            >
              <option value="all">All Items</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <button onClick={generateReport} className="generate-button">
            Generate Report
          </button>
        </div>
        <p className="date-help-text">
          Select a date range and category to filter expenses. The report will show all expenses between the selected dates (inclusive) and matching the selected category.
        </p>
      </div>

      {error && <div className="error">{error}</div>}
      
      {reportData && reportData.expenses.length === 0 && (
        <div className="no-data-message">
          <p>No expenses found for the selected date range.</p>
          <p>Please try selecting different dates or check if there are expenses in your database.</p>
        </div>
      )}

      {/* Debug information - remove this in production */}
      {process.env.NODE_ENV === 'development' && (
        <div className="debug-info" style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
          <h4>Debug Info (Development Only)</h4>
          <p><strong>Total expenses loaded:</strong> {expenses.length}</p>
          <p><strong>Sample expense date format:</strong> {expenses[0]?.date ? JSON.stringify(expenses[0].date) : 'No date'}</p>
          <p><strong>Sample expense createdAt:</strong> {expenses[0]?.createdAt ? JSON.stringify(expenses[0].createdAt) : 'No createdAt'}</p>
          <p><strong>Selected date range:</strong> {startDate} to {endDate}</p>
        </div>
      )}

      {reportData && (
        <div className="report-content">
          <div className="report-summary">
            <div className="summary-card">
              <h3>Total Expenses</h3>
              <p className="amount">{formatAmount(reportData.totalAmount)}</p>
              <p className="period">
                {formatDate(reportData.startDate)} - {formatDate(reportData.endDate)}
              </p>
            </div>
            <div className="summary-card">
              <h3>Average Daily Expenses</h3>
              <p className="amount">{formatAmount(reportData.averageDailyExpense)}</p>
            </div>
          </div>

                     <div className="category-breakdown">
             <h3>Item Breakdown</h3>
            <div className="category-chart">
              {reportData.categoryBreakdown.length > 0 ? (
                reportData.categoryBreakdown.map(category => (
                  <div key={category.id} className="category-bar-container">
                    <div className="category-bar-header">
                      <div className="category-info">
                        <span className="category-icon" style={{ backgroundColor: category.color || '#95A5A6' }}>
                          {getIconComponent(category.icon)}
                        </span>
                        <span className="category-name">{category.name || 'Other'}</span>
                      </div>
                      <div className="category-stats">
                        <span className="category-amount">{formatAmount(category.amount)}</span>
                        <span className="category-percentage">
                          {category.percentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="category-bar">
                      <div
                        className="category-bar-fill"
                        style={{
                          width: `${category.percentage}%`,
                          backgroundColor: category.color || '#95A5A6'
                        }}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-categories-message">
                  <p>No items found for the selected date range.</p>
                </div>
              )}
            </div>
          </div>

          <div className="detailed-expenses">
            <h3>Detailed Expenses</h3>
            <div className="expense-table">
              <table>
                                 <thead>
                   <tr>
                     <th>Date</th>
                     <th>Item</th>
                     <th>Amount</th>
                   </tr>
                 </thead>
                                 <tbody>
                   {reportData.expenses.map(expense => (
                     <tr key={expense.id}>
                       <td>{formatDate(expense.date)}</td>
                       <td>
                         <span className="table-category">
                           <span className="category-dot" style={{ backgroundColor: (categories.find(c => c.id === expense.category)?.color) || '#95A5A6' }}></span>
                           {(categories.find(c => c.id === expense.category)?.name) || 'Other'}
                         </span>
                       </td>
                       <td className="amount-cell">{formatAmount(expense.amount)}</td>
                     </tr>
                   ))}
                 </tbody>
              </table>
            </div>
          </div>

          <div className="stock-usage-section">
            <div className="section-header">
              <h3>Stock Usage Analysis</h3>
              <button
                onClick={() => setShowStockAnalysis(!showStockAnalysis)}
                className="toggle-button"
              >
                {showStockAnalysis ? 'Hide' : 'Show'} Stock Analysis
              </button>
            </div>
            {showStockAnalysis && (
              <div className="stock-analysis-content">
                {expensesWithStock.length === 0 ? (
                  <p className="no-stock-message">No stock-related expenses found</p>
                ) : (
                  <>
                    <div className="stock-summary">
                      <div className="summary-item">
                        <span className="summary-label">Stock In Expenses:</span>
                        <span className="summary-value positive">
                          {formatAmount(expensesWithStock.filter(e => e.expenseType === 'stock_in').reduce((sum, e) => sum + (e.amount || 0), 0))}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Stock Out Expenses:</span>
                        <span className="summary-value negative">
                          {formatAmount(expensesWithStock.filter(e => e.expenseType === 'stock_out').reduce((sum, e) => sum + (e.amount || 0), 0))}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Total Stock Expenses:</span>
                        <span className="summary-value">
                          {formatAmount(expensesWithStock.reduce((sum, e) => sum + (e.amount || 0), 0))}
                        </span>
                      </div>
                    </div>
                    <div className="stock-expenses-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Expense</th>
                            <th>Type</th>
                            <th>Stock Item</th>
                            <th>Amount</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expensesWithStock.map(expense => {
                            const stockItem = stockItems.find(item => item.id === expense.stockItemId);
                            return (
                              <tr key={expense.id}>
                                <td>{expense.title || 'No title'}</td>
                                <td>
                                  <span className={`expense-type-badge ${expense.expenseType === 'stock_in' ? 'stock-in' : 'stock-out'}`}>
                                    {expense.expenseType === 'stock_in' ? 'Stock In' : 'Stock Out'}
                                  </span>
                                </td>
                                <td>{stockItem?.name || 'Unknown Item'}</td>
                                <td>{formatAmount(expense.amount || 0)}</td>
                                <td>{formatDate(expense.date)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/*<div className="report-actions">
            <button onClick={downloadReport} className="download-button">
              Download Report
            </button>
          </div>*/}
        </div>
      )}
    </div>
  );
};

export default ExpenseReport; 